import React, { useEffect } from "react";
import { Route } from "react-router-dom";
import Error404 from "components/Error404";
import { useParams } from "react-router";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import ItemTemp from "@outplayed/tooltips/src/tooltipImages/ItemTemp";
import { isMythicItem } from "../ItemParsers";
import RecipeBranch from "./RecipeBranch";
import ItemCard from "../ItemCard";
import ItemDetails from "./ItemDetails";
import ItemBuildsInto from "./ItemBuildsInto";
import ItemCostAnalysis from "./ItemCostAnalysis";
import { getItemDescription } from "./getItem";
import { useItemSEO } from "@ugg/shared/utils/seo-helpers";

const ItemPage = (props) => {
  const { useNewRiotItems, getItemIdFromName, useItemsWiki } = getRiotAssetsContext();
  const getItemTitleAndDesc = useItemSEO({ ssr: true });
  const { itemName } = useParams();
  const itemsList = useItemsWiki({ ssr: true });

  const data = itemsList && itemsList.data;

  const { data: items, loading: fetchingItems, error: errorItems } = useNewRiotItems({ ssr: true });
  const itemId = getItemIdFromName(itemName, { optionalData: items });

  const itemData = items && items[itemId];
  const itemDescription = getItemDescription(itemId, itemData, data, { render: true });

  const removedItems = {
    Emberknife: 1035,
    Hailblade: 1039,
    "Obsidian Edge": 1040,
    "Penetrating Bullets": 1500,
    Fortification: 1501,
    "Reinforced Armor": 1502,
    Vanguard: 1504,
    "Lightning Rod": 1505,
    Overcharged: 1507,
    "Anti-tower Socks": 1508,
    Gusto: 1509,
    "Phreakish Gusto": 1510,
    "Super Mech Armor": 1511,
    "Super Mech Power Field": 1512,
    "Turret Plating": 1515,
    "Structure Bounty": 1516,
    OverchargedHA: 1520,
    "Tower Power-up": 1522,
    "Warden's Eye": 1503,
    OvererchargedHA: 1520,
    "Tower Power-Up": 1522,
    "Scarecrow Effigy": 3330,
    "The Golden Spatula": 4403,
    "Stirring Wardstone": 4641,
    Bloodward: 7008,
    "Sandshrike's Claw": 7000,
    "Wyrmfallen Sacrifice": 7007,
    "Seat of Command": 7022,
  };
  const removedItemsIds = Object.values(removedItems);

  //Remove "into" array if item it goes "into" doesn't exist anymore
  for (var key in items) {
    if (items[key].hasOwnProperty("into") && removedItemsIds.includes(parseInt(items[key]["into"]?.[0]))) {
      items[key]["into"] = [];
    }
  }

  //Manually adding description for Imperial Mandate
  if (items?.hasOwnProperty(4005)) {
    items[4005]["into"] = [];
    items[4005]["description"] =
      "<mainText><stats><attention>40</attention> Ability Power<br><attention>200</attention> Health<br><attention>20</attention> Ability Haste<br><attention>100%</attention> Base Mana Regen</stats><br><li><passive>Coordinated Fire:</passive> Abilities that <status>Slow</status> or <status>Immobilize</status> a champion deal bonus damage and mark them. Ally champion damage detonates the mark, dealing additional damage and granting you both Move Speed. </mainText><br>";
  }
  // Scroll to top
  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [itemId]);

  if (fetchingItems) {
    return (
      <div className="spinthatshit-loader" style={{ height: "85vh" }}>
        <div className="spinner" />
      </div>
    );
  }

  if (itemId === null) {
    return <Route component={Error404} />;
  }

  if (!itemData || !data) {
    return <div className="items-home-page"></div>;
  }

  const isMythic = isMythicItem(itemData?.description);

  const itemProps = {
    itemId: itemId,
    className: isMythic ? "items-pic-xlarge items-border-mythic-large" : "items-pic-xlarge items-border-normal-large",
    enableLink: true,
  };

  const itemCardProps = {
    itemId: itemId,
    isMainItem: true,
    isMythic,
  };

  const itemDataProps = {
    itemId: itemId,
    itemData,
    isMythic,
  };

  const itemComponents = itemData?.from ? itemData.from : itemData?.specialRecipe ? [itemData?.specialRecipe] : [];

  const getItemComponents = (itemId) => {
    const compData = items && items[itemId];
    return compData?.from ? compData.from : compData?.specialRecipe ? [compData.specialRecipe] : [];
  };

  const itemMap = (originalItemId, itemComponents) => {
    // original item (0-DEPTH)
    const map = { [originalItemId]: [] };

    // looping over each item component (1-DEPTH)
    for (let i = 0; i < itemComponents.length; i++) {
      const compId = itemComponents[i];
      const components = getItemComponents(compId);

      if (components?.length > 0) {
        const tempMap = { [compId]: [] };
        // looping over each item component (2-DEPTH)
        for (let j = 0; j < components.length; j++) {
          const compId2 = components[j];
          const components2 = getItemComponents(compId2);
          tempMap[compId].push({ [compId2]: components2 });
        }
        map[itemId].push(tempMap);
      } else {
        map[itemId].push({ [compId]: components });
      }
    }

    return map;
  };

  // TODO itemId:7024 sometimes bugs on the recipe tree when page refreshed
  const map = itemMap(itemId, itemComponents);

  const itemsBuildsInto = itemData.into;

  return (
    <div className="items-home-page">
      <div className="items-home content-side-padding">
        {getItemTitleAndDesc(itemName)}
        <div className="items-home-header">
          <div className="items-home-header-pic">
            <ItemTemp {...itemProps} />
          </div>
          <div className="items-header-text">
            <h1>{itemData?.name}</h1>
            {itemDescription}
          </div>
        </div>

        <div className="items-container">
          {itemComponents && (
            <div className="items-section items-recipe">
              <div className="items-header">Recipe</div>
              <div className="recipe-container">
                {map && (
                  <div>
                    <ItemCard {...itemCardProps} />
                    {itemComponents?.map((componentId, index) => (
                      <RecipeBranch
                        key={`${componentId}-${index}-1`}
                        itemId={componentId}
                        index={index}
                        depth={1}
                        mapBranch={map[itemId]}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <ItemDetails {...itemDataProps} />

          {itemsBuildsInto && itemsBuildsInto.length > 0 && <ItemBuildsInto {...itemDataProps} />}

          <ItemCostAnalysis {...itemDataProps} />
        </div>
      </div>
    </div>
  );
};

export default ItemPage;
